import { z } from 'zod';
import type { FieldConflict } from './merge.ts';
import type { SystemFields } from './records.ts';

/**
 * Sync wire protocol.
 *
 * Push: the client sends queued mutations in order. Each has a unique id
 * (`mid`) used as an idempotency key — replays return the original result and
 * never apply twice.
 *
 * Pull: the client asks for changes after its per-family cursor. The server
 * returns only records the user may read, tombstones for deletions they could
 * see, and removals for records they lost access to.
 */

export const MAX_MUTATIONS_PER_PUSH = 200;

export const mutationSchema = z.object({
  mid: z.uuid(),
  collection: z.string().regex(/^[a-z_]{1,40}$/),
  id: z.uuid(),
  op: z.enum(['upsert', 'delete', 'restore']),
  /** Changed fields (data fields plus audience/sharedWith). Required for upsert. */
  patch: z.record(z.string(), z.unknown()).optional(),
  /** Record revision the client based this change on (0 for new records). */
  baseRev: z.number().int().min(0),
  /** Client edit time in ms; the server clamps it to its own clock. */
  clientTime: z.number().int().min(0),
});
export type Mutation = z.infer<typeof mutationSchema>;

export const pushRequestSchema = z.object({
  familyId: z.uuid(),
  mutations: z.array(mutationSchema).min(1).max(MAX_MUTATIONS_PER_PUSH),
});
export type PushRequest = z.infer<typeof pushRequestSchema>;

/** A full record as sent over the wire (system fields + data fields). */
export type WireRecord = SystemFields & Record<string, unknown>;

export type RejectCode = 'forbidden' | 'not_found' | 'invalid' | 'deleted' | 'module_disabled' | 'conflict_family';

export type MutationResult =
  | {
      mid: string;
      status: 'applied';
      record: WireRecord;
      conflicts?: FieldConflict[];
      superseded?: string[];
    }
  | {
      mid: string;
      status: 'deleted';
      collection: string;
      id: string;
      rev: number;
    }
  | {
      mid: string;
      status: 'rejected';
      code: RejectCode;
      message: string;
      /** Current server copy, when the user may still read it (to roll back optimistic UI). */
      record?: WireRecord;
    };

export interface PushResponse {
  results: MutationResult[];
  serverTime: number;
}

export interface PulledChange {
  collection: string;
  record?: WireRecord;
  /** Present instead of `record` for deletions. */
  deleted?: { id: string; rev: number };
}

export interface PullResponse {
  familyId: string;
  changes: PulledChange[];
  /** Records the user can no longer see (audience change, role change). */
  removals: { collection: string; id: string }[];
  cursor: number;
  hasMore: boolean;
  /** Changes when the user's access in this family changes; client must resync fully. */
  accessEpoch: number;
  serverTime: number;
  /** The client's cursor was ahead of the server (restored backup): rebuild from scratch. */
  reset?: boolean;
}

export const pullQuerySchema = z.object({
  familyId: z.uuid(),
  since: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
});
